import { formatCopy } from '../../copy/format.js';
import copy from '../../copy/en/auth.json' with { type: 'json' };
/**
 * /mfa — TOTP enrollment, challenge and status on top of supabase-js MFA.
 *
 *   enroll  → supabase.auth.mfa.enroll({ factorType: "totp" }) → QR + secret
 *           → challenge({ factorId }) → verify({ factorId, challengeId, code })
 *   verify  → supabase.auth.mfa.challengeAndVerify({ factorId, code }) when a
 *             verified factor exists and the session is aal1
 *   status  → getAuthenticatorAssuranceLevel() + listFactors()
 *
 * After a successful verify the session token is aal2; we refetch `/api/me`
 * and return the user to where they were heading. Supabase TOTP has no
 * recovery codes: the admin reset path is server-side (later in Phase 0).
 */
import type { Factor } from "@supabase/supabase-js";
import { KeyRound, ShieldCheck, Smartphone } from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import { Badge } from "../components/ui/Badge.js";
import { Button } from "../components/ui/Button.js";
import { Callout } from "../components/ui/Callout.js";
import { CopyButton } from "../components/ui/CopyButton.js";
import { Disclosure } from "../components/ui/Disclosure.js";
import { Field, Input } from "../components/ui/Form.js";
import { KeyValueGrid } from "../components/ui/KeyValue.js";
import { QrImage } from "../components/ui/QrImage.js";
import { Spinner } from "../components/ui/FullPage.js";
import { fmtDate } from "../lib/format.js";
import { Brand } from "../shell/Brand.js";
import { ModeToggle } from "../shell/TopBar.js";
import { useMe } from "./MeProvider.js";
import { useSession } from "./SessionProvider.js";

type Aal = "aal1" | "aal2";

interface Enrollment {
  factorId: string;
  qrCode: string;
  secret: string;
  uri: string;
}

export function MfaPage() {
  const { supabase, authMode, signOut, clearMfaChallenge } = useSession();
  const { me, refetch, mfaBlocked } = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [loading, setLoading] = useState(true);
  const [factors, setFactors] = useState<Factor[]>([]);
  const [aal, setAal] = useState<Aal>("aal1");
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [{ data: f, error: fe }, { data: a, error: ae }] = await Promise.all([
        supabase.auth.mfa.listFactors(),
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      ]);
      if (fe) throw fe;
      if (ae) throw ae;
      setFactors(f?.totp ?? []);
      setAal(a?.currentLevel === "aal2" ? "aal2" : "aal1");
    } catch (e) {
      setError(e instanceof Error ? e.message : copy.MfaPage.couldNotLoadMfaStatus);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    void load();
  }, [load]);

  const verified = factors.filter((f) => f.status === "verified");
  const unverified = factors.filter((f) => f.status !== "verified");

  async function afterVerified() {
    clearMfaChallenge();
    await refetch();
    setDone(true);
    setCode("");
    setEnrollment(null);
    await load();
  }

  async function startEnrollment() {
    if (!supabase) return;
    setBusy(true);
    setError(null);
    try {
      // A dangling unverified factor blocks re-enrollment under the same name.
      for (const f of unverified) await supabase.auth.mfa.unenroll({ factorId: f.id });
      const { data, error: err } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: "Authenticator app",
      });
      if (err) throw err;
      if (!data || data.type !== "totp") throw new Error(copy.MfaPage.unexpectedEnrollmentResponse);
      setEnrollment({ factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret, uri: data.totp.uri });
    } catch (e) {
      setError(e instanceof Error ? e.message : copy.MfaPage.couldNotStartEnrollment);
    } finally {
      setBusy(false);
    }
  }

  async function verifyEnrollment(e: FormEvent) {
    e.preventDefault();
    if (!supabase || !enrollment) return;
    if (!/^\d{6}$/.test(code.trim())) {
      setError(copy.MfaPage.enterThe6DigitCodeFromYour);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data: ch, error: ce } = await supabase.auth.mfa.challenge({ factorId: enrollment.factorId });
      if (ce) throw ce;
      const { error: ve } = await supabase.auth.mfa.verify({
        factorId: enrollment.factorId,
        challengeId: ch.id,
        code: code.trim(),
      });
      if (ve) throw ve;
      await afterVerified();
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.MfaPage.thatCodeDidNotWorkTryThe);
    } finally {
      setBusy(false);
    }
  }

  async function verifyChallenge(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    const factor = verified[0];
    if (!factor) return;
    if (!/^\d{6}$/.test(code.trim())) {
      setError(copy.MfaPage.enterThe6DigitCodeFromYour);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.mfa.challengeAndVerify({ factorId: factor.id, code: code.trim() });
      if (err) throw err;
      await afterVerified();
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.MfaPage.thatCodeDidNotWorkTryThe);
    } finally {
      setBusy(false);
    }
  }

  async function removeFactor(factorId: string) {
    if (!supabase) return;
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.mfa.unenroll({ factorId });
      if (err) throw err;
      await refetch();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.MfaPage.couldNotRemoveTheAuthenticator);
    } finally {
      setBusy(false);
    }
  }

  const serverStatus = me?.mfa;
  const enforcedBlock = mfaBlocked || Boolean(serverStatus?.enforced && serverStatus.aal !== "aal2");

  return (
    <div className="fullpage">
      <div className="fullpage__mode">
        <ModeToggle />
      </div>
      <div className="fullpage__card fullpage__card--wide">
        <div className="fullpage__hero">
          <Brand to={null} />
          <h1 className="fullpage__title">{copy.MfaPage.twoFactorAuthentication}</h1>
          <p className="fullpage__sub">
            {me?.email ? <>{copy.MfaPage.signedInAs}<strong>{me.email}</strong>.</> : copy.MfaPage.protectYourStaffAccountWithAnAuthenticator}
          </p>
        </div>

        <div className="stack">
          {authMode === "mock" || !supabase ? (
            <Callout tone="info" title={copy.MfaPage.notAvailableInMockAuthMode}>{copy.MfaPage.totpEnrollmentAndChallengesRunThroughSupabase}</Callout>
          ) : loading ? (
            <div className="row" style={{ justifyContent: "center", padding: "var(--space-6)" }}>
              <Spinner /> <span className="muted">{copy.MfaPage.loadingMfaStatus}</span>
            </div>
          ) : (
            <>
              {enforcedBlock && aal !== "aal2" ? (
                <Callout tone="warning" title={copy.MfaPage.verificationRequired}>{copy.MfaPage.yourSessionMustBeVerifiedWithA}</Callout>
              ) : null}
              {done ? (
                <Callout tone="success" title={copy.MfaPage.verified}>{copy.MfaPage.thisSessionIsNowProtectedWithTwo}</Callout>
              ) : null}
              {error ? <Callout tone="danger">{error}</Callout> : null}

              {/* Status panel */}
              <KeyValueGrid
                items={[
                  {
                    key: "session",
                    label: copy.MfaPage.thisSession,
                    value: aal === "aal2" ? <Badge tone="success">{copy.MfaPage.verifiedAal2}</Badge> : <Badge tone="warning">{copy.MfaPage.passwordOnlyAal1}</Badge>,
                  },
                  {
                    key: "factor",
                    label: copy.MfaPage.authenticator,
                    value: verified.length ? <Badge tone="success">{copy.MfaPage.enrolled}</Badge> : <Badge tone="neutral">{copy.MfaPage.notEnrolled}</Badge>,
                  },
                  {
                    key: "policy",
                    label: copy.MfaPage.policy,
                    value: serverStatus
                      ? serverStatus.enforced
                        ? copy.MfaPage.requiredNow
                        : serverStatus.required
                          ? (serverStatus.graceEndsAt ? formatCopy(copy.MfaPage.requiredUntil, { date: fmtDate(serverStatus.graceEndsAt) }) : copy.MfaPage.required)
                          : copy.MfaPage.optional
                      : copy.MfaPage.notReportedByTheServerYet,
                  },
                ]}
              />

              {aal === "aal2" && verified.length > 0 && !enrollment ? (
                <div className="stack stack--sm">
                  {verified.map((f) => (
                    <div key={f.id} className="row row--between card" style={{ padding: "var(--space-3) var(--space-4)" }}>
                      <span className="row">
                        <ShieldCheck width={16} height={16} aria-hidden style={{ color: "var(--color-success)" }} />
                        <span>{f.friendly_name || copy.MfaPage.authenticatorApp}</span>
                        <span className="muted text-xs">{formatCopy(copy.MfaPage.added, { date: fmtDate(f.created_at) })}</span>
                      </span>
                      <Button size="sm" variant="secondary" onClick={() => void removeFactor(f.id)} disabled={busy}>{copy.MfaPage.remove}</Button>
                    </div>
                  ))}
                  <p className="text-xs muted">{copy.MfaPage.supabaseTotpHasNoRecoveryCodesIf}</p>
                </div>
              ) : null}

              {aal !== "aal2" && verified.length > 0 ? (
                <form className="stack" onSubmit={(e) => void verifyChallenge(e)} noValidate>
                  <div className="row">
                    <Smartphone width={18} height={18} aria-hidden style={{ color: "var(--color-accent-figure)" }} />
                    <strong>{copy.MfaPage.enterTheCodeFromYourAuthenticatorApp}</strong>
                  </div>
                  <Field label={copy.MfaPage.message6DigitCode}>
                    {(control) => (
                      <Input
                        {...control}
                        className="otp-input"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                        autoFocus
                      />
                    )}
                  </Field>
                  <div className="form-actions">
                    <Button type="submit" variant="primary" loading={busy}>{copy.MfaPage.verify}</Button>
                  </div>
                </form>
              ) : null}

              {verified.length === 0 && !enrollment ? (
                <div className="stack">
                  <p className="text-sm secondary">{copy.MfaPage.youWillScanAQrCodeWith}</p>
                  <div className="form-actions" style={{ justifyContent: "flex-start", marginTop: 0 }}>
                    <Button variant="primary" icon={KeyRound} onClick={() => void startEnrollment()} loading={busy}>{copy.MfaPage.setUpAuthenticator}</Button>
                  </div>
                </div>
              ) : null}

              {enrollment ? (
                <form className="stack" onSubmit={(e) => void verifyEnrollment(e)} noValidate>
                  <div className="qr-panel">
                    {enrollment.qrCode.startsWith("data:") ? (
                      <img
                        className="qr-box"
                        src={enrollment.qrCode}
                        alt={copy.MfaPage.authenticatorEnrollmentQrCode}
                        style={{ objectFit: "contain" }}
                      />
                    ) : (
                      <QrImage svg={enrollment.qrCode} label={copy.MfaPage.authenticatorEnrollmentQrCode} />
                    )}
                    <div className="qr-panel__side stack stack--sm">
                      <strong>{copy.MfaPage.message1ScanThisCode}</strong>
                      <Disclosure label={copy.MfaPage.canTScanTheCode}>
                        <p className="text-sm muted">{copy.MfaPage.enterThisSecretManually}</p>
                        <div className="copy-row">
                          <Input mono readOnly value={enrollment.secret} aria-label={copy.MfaPage.totpSecret} />
                          <CopyButton value={enrollment.secret} />
                        </div>
                        <a className="text-xs" href={enrollment.uri}>{copy.MfaPage.openInAnAuthenticatorApp}</a>
                      </Disclosure>
                    </div>
                  </div>
                  <strong>{copy.MfaPage.message2EnterThe6DigitCodeIt}</strong>
                  <Field label={copy.MfaPage.message6DigitCode}>
                    {(control) => (
                      <Input
                        {...control}
                        className="otp-input"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      />
                    )}
                  </Field>
                  <div className="form-actions">
                    <Button variant="ghost" onClick={() => setEnrollment(null)} disabled={busy}>{copy.MfaPage.cancel}</Button>
                    <Button type="submit" variant="primary" loading={busy}>{copy.MfaPage.confirmAndEnable}</Button>
                  </div>
                </form>
              ) : null}
            </>
          )}

          <div className="row row--between" style={{ marginTop: "var(--space-2)" }}>
            <Button variant="ghost" onClick={() => void signOut(copy.MfaPage.youHaveBeenSignedOut)}>{copy.MfaPage.signOut}</Button>
            <Button
              variant={aal === "aal2" || !enforcedBlock ? "primary" : "secondary"}
              onClick={() => navigate(from === "/mfa" ? "/" : from)}
              disabled={enforcedBlock && aal !== "aal2"}
            >{copy.MfaPage.continueToThePortal}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
